import React, { useEffect, useMemo, useState } from 'react';
import moment, { Moment } from "moment";
import { Column, SpaceBetweenRow } from '@shared/ui/atoms/StructuralLayout';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import Colours from '@shared/ui/atoms/Colours';
import { Period, toSideOfDay } from '@features/whereabouts/models/movements.models';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { useSelector } from "react-redux";
import { selectConfig, selectHasGlobalConfigLoaded } from '@shared/store/config.duck';
import { fetchUsersHolidayInfoV2, getEstimatedWorkDaysBooked } from '@features/holidays/services/HolidayService';
import { TrackJS } from "trackjs";
import { UserHolidayInfoResponse } from "../models/Holidays.model";
import { selectCalendarDate } from '@shared/store/dashboard.duck';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { HOLIDAY_ID } from '@features/whereabouts/services/WhereaboutOptions';
import { useTranslation } from "react-i18next";
import { roundDays } from '@shared/utils/MathUtils';

export function HolidayBreakdownSummary(props: Props) {
  const {fromDate, toDate, userId, startDatePeriod, endDatePeriod} = props;
  const calendarDate = useSelector(selectCalendarDate);
  const config = useSelector(selectConfig);
  const isConfigLoading = useSelector(selectHasGlobalConfigLoaded);
  const [usersHolidayInfo, setUsersHolidayInfo] = useState<UserHolidayInfoResponse | undefined>(undefined);
  const [dayCount, setDayCount] = useState(0);
  const [dayCountLoading, setDayCountLoading] = useState(false);
  const showTotalDaysBooked = props.selectedWhereaboutsTypeId === HOLIDAY_ID;
  const {t} = useTranslation();

  const holidayStartDate = useMemo(() => {
    if (fromDate) {
      return config.getHolidayYearStartDate(fromDate)
    }
    else if (calendarDate) {
      return config.getHolidayYearStartDate(calendarDate);
    }
    else {
      return config.getHolidayYearStartDate(moment());
    }
  }, [calendarDate, config, fromDate]);
  const holidayStartDateString = holidayStartDate?.format(DATE_FORMAT)

  const holidayEndDate = useMemo(() => {
    if (fromDate) return config.getHolidayYearEndDate(fromDate)
    else if (calendarDate) return config.getHolidayYearEndDate(calendarDate);
    else config.getHolidayYearEndDate(moment());
  }, [calendarDate, config, fromDate]);

  const holidayYear = useMemo(() => {
    if (holidayStartDate?.year() === holidayEndDate?.year()) {
      return holidayStartDate.year();
    } else {
      return `${holidayStartDate?.year()} - ${holidayEndDate?.year()}`
    }
  }, [holidayStartDate, holidayEndDate])

  useEffect(() => {
    if (holidayStartDateString && userId && isConfigLoading) {
      fetchUsersHolidayInfoV2(holidayStartDateString, userId)
        .then(setUsersHolidayInfo)
        .catch((err) => {
          setUsersHolidayInfo(undefined);
          TrackJS?.track(err);
        })
    }
  }, [holidayStartDateString, userId, isConfigLoading]);

  useEffect(() => {
    if (fromDate && toDate && userId && calendarDate && showTotalDaysBooked) {
      setDayCountLoading(true)
      getEstimatedWorkDaysBooked({
        userId: userId,
        dateFrom: fromDate.format(DATE_FORMAT),
        dateTo: toDate.format(DATE_FORMAT),
        firstDaySideOfDay: toSideOfDay(startDatePeriod),
        lastDaySideOfDay: toSideOfDay(endDatePeriod),
      }).then(response => {
        setDayCount(response.daysUsed);
      })
      .finally(() => setDayCountLoading(false))
    }
  }, [calendarDate, endDatePeriod, fromDate, showTotalDaysBooked, startDatePeriod, toDate, userId]);

  const carryOverDaysRemaining = roundDays(Math.max(0, (usersHolidayInfo?.autoCarryOverInfo?.daysCarriedOver ?? 0) - (usersHolidayInfo?.holidaysTaken ?? 0)));

  const daysLeft = useMemo(() => {
    if (!usersHolidayInfo) return '??';
    if (config.autoCarryOverEnabled) {
      const daysCarriedOver = usersHolidayInfo?.autoCarryOverInfo?.daysCarriedOver ?? 0;
      const usedOfCarryOver = daysCarriedOver - (usersHolidayInfo.holidaysTaken + dayCount);
      return roundDays(Math.min(usersHolidayInfo.holidayAllowance + usedOfCarryOver, usersHolidayInfo.holidayAllowance));
    } else {
      return roundDays(usersHolidayInfo.holidayAllowance - usersHolidayInfo.holidaysTaken - dayCount)
    }
  }, [usersHolidayInfo, dayCount, config]);

  const hasCarryOver = config.autoCarryOverEnabled && usersHolidayInfo?.autoCarryOverInfo;

  if (!showTotalDaysBooked && !config.holidayAllowance) {
    return null;
  }

  return (
    <Column style={props.style ?? {}}>
      {showTotalDaysBooked && <>
        <SpaceBetweenRow style={{borderBottom: `1px solid ${Colours.mildGrey}`}}>
          <BodyRegular weight={400} colour={Colours.black}>{t('allowance.total-days-booked')}</BodyRegular>
          <BodyRegular weight={600} colour={Colours.black}>
            {dayCountLoading ? <SimpleSpinner size={16} /> : <>{t('allowance.days', {days: dayCount})}</>}
          </BodyRegular>
        </SpaceBetweenRow>
      </>}

      {config.holidayAllowance && <>
        <SpaceBetweenRow>
          <BodyRegular weight={400} colour={Colours.black}>{t(hasCarryOver ? 'allowance.standard-remaining-days' : 'allowance.remaining-days')} ({holidayYear})</BodyRegular>
          <BodyRegular weight={600} colour={Colours.black}>{t('allowance.days', {days: daysLeft})}</BodyRegular>
        </SpaceBetweenRow>

        {hasCarryOver && <>
          <SpaceBetweenRow style={{borderTop: 'solid', borderWidth: '1px', borderColor: Colours.mildGrey}}>
            <BodyRegular weight={400} colour={Colours.black}>{t('allowance.carry-over-remaining')}</BodyRegular>
            <BodyRegular weight={600} colour={Colours.black}>{t('allowance.days', {days: carryOverDaysRemaining})}</BodyRegular>
          </SpaceBetweenRow>
        </>}

      </>}

    </Column>
  )
}

interface Props {
  fromDate?: Moment
  toDate?: Moment;
  style?: any;
  userId?: string;
  startDatePeriod: Period;
  endDatePeriod: Period;
  selectedWhereaboutsTypeId: number;
}
